import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserData, CRM_OWNER_EXECUTIVE_ROLES } from '../services/accessControlService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { TEAM_ALACARTE_DEPARTMENTS } from '../utils/teamAlaCarteRoutes';
import BBALoader from './BBALoader';

/**
 * Owner/Executive or internal user whose `internal_users.team` matches `team`
 * (one team, or a list of teams that share the page).
 * @param {{ children: React.ReactNode, team: ('Marketing'|'Finance'|'HR') | Array<'Marketing'|'Finance'|'HR'> }} props
 */
const CrmTeamAlaCarteRoute = ({ children, team }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const location = useLocation();
  const [allowed, setAllowed] = useState(null);
  const teamsKey = Array.isArray(team) ? team.join('|') : team;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (authLoading) return;
      if (!currentUser) {
        if (!cancelled) setAllowed(false);
        return;
      }
      const teams = teamsKey.split('|');
      if (!teams.every((t) => TEAM_ALACARTE_DEPARTMENTS.includes(t))) {
        if (!cancelled) setAllowed(false);
        return;
      }
      try {
        const userData = await getUserData(currentUser.uid);
        if (!userData || userData.userType !== 'internal') {
          if (!cancelled) setAllowed(false);
          return;
        }
        const role = userData.role || '';
        if (CRM_OWNER_EXECUTIVE_ROLES.includes(role)) {
          if (!cancelled) setAllowed(true);
          return;
        }
        const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
        const internalTeam = snap.exists() ? snap.data().team || '' : '';
        if (!cancelled) setAllowed(teams.includes(internalTeam));
      } catch (e) {
        console.error(e);
        if (!cancelled) setAllowed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, authLoading, teamsKey]);

  if (authLoading || allowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BBALoader size={96} />
      </div>
    );
  }

  if (!currentUser || !allowed) {
    return <Navigate to="/crm" replace state={{ from: location }} />;
  }

  return children;
};

export default CrmTeamAlaCarteRoute;
